/**
 * loans.service.ts
 *
 * Typed API call wrappers for the loans domain.
 * All methods use the shared apiClient (Axios instance with JWT interceptor).
 */
import { apiClient } from './apiClient';
import type { PaginatedResponse } from '@/types/api.types';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface Loan {
  loanId: number;
  accountId: number;
  patientId: number | null;
  planId: number | null;
  giftCardId: number | null;
  loanType: string | null;
  loanAmount: number;
  loanInterval: string | null;
  loanMonths: number | null;
  intervalAmount: number | null;
  lateFee: number | null;
  interestRate: number | null;
  interestType: string | null;
  createDate: string;
  endDate: string | null;
  paidDate: string | null;
  loanStatus: string | null;
  userId: number | null;
  // Computed
  paidAmount: number;
  balance: number;
  // Joined
  patientFirstName: string | null;
  patientLastName: string | null;
  planName: string | null;
}

export interface LoanListParams {
  page?: number;
  pageSize?: number;
  perPage?: number;
  status?: string;
  patientId?: number;
  search?: string;
  dateFrom?: string;
  dateTo?: string;
}

export interface CreateLoanPayload {
  patientId?: number | null;
  planId?: number | null;
  giftCardId?: number | null;
  loanType?: string | null;
  loanAmount: number;
  loanInterval?: string | null;
  loanMonths?: number | null;
  intervalAmount?: number | null;
  lateFee?: number | null;
  interestRate?: number | null;
  interestType?: string | null;
  endDate?: string | null;
  loanStatus?: string | null;
}

export interface UpdateLoanPayload {
  patientId?: number | null;
  planId?: number | null;
  giftCardId?: number | null;
  loanType?: string | null;
  loanAmount?: number;
  loanInterval?: string | null;
  loanMonths?: number | null;
  intervalAmount?: number | null;
  lateFee?: number | null;
  interestRate?: number | null;
  interestType?: string | null;
  endDate?: string | null;
  paidDate?: string | null;
  loanStatus?: string | null;
}

// ─── API wrappers ─────────────────────────────────────────────────────────────

export async function listLoans(
  params?: LoanListParams,
): Promise<PaginatedResponse<Loan>> {
  const { pageSize, perPage, ...rest } = params ?? {};
  const res = await apiClient.get<PaginatedResponse<Loan>>('/loans', {
    params: {
      ...rest,
      perPage: perPage ?? pageSize,
    },
  });
  return res.data;
}

export async function getLoan(id: number): Promise<Loan> {
  const res = await apiClient.get<{ data: Loan }>(`/loans/${id}`);
  return res.data.data;
}

export async function createLoan(payload: CreateLoanPayload): Promise<Loan> {
  const res = await apiClient.post<{ data: Loan }>('/loans', payload);
  return res.data.data;
}

export async function updateLoan(id: number, payload: UpdateLoanPayload): Promise<Loan> {
  const res = await apiClient.patch<{ data: Loan }>(`/loans/${id}`, payload);
  return res.data.data;
}

export async function deleteLoan(id: number): Promise<void> {
  await apiClient.delete(`/loans/${id}`);
}

export interface LoanListStats {
  total: number;
  active: number;
  totalBalance: number;
  pastDue: number;
}

export function getLoanStats(): Promise<LoanListStats> {
  return apiClient
    .get<{ data: LoanListStats }>('/loans/stats')
    .then((r) => r.data.data);
}
